/**
 * Shared user-facing copy for states more than one page renders identically.
 *
 * Ported from `lib/constants/messages.ts` — the plan flagged it and `brand.ts` as UI copy that
 * belongs with the frontend rather than the domain port, which is why neither has a Python
 * counterpart.
 *
 * The point of the module is that every surface receiving a given status says the same thing.
 * A page that writes its own 403 copy is a page that will eventually offer a retry button for
 * a state retrying cannot change.
 */

/**
 * A 403 from an allowlist-gated route. **Never present this as retryable** — retrying will not
 * succeed until an admin adds the account. The wording is specified in the doc comment on
 * `require_allowed_user`.
 */
export const NOT_AUTHORIZED_MESSAGE =
  "You're signed in, but your account hasn't been authorized yet. " +
  "Please contact your Jones + Palmer contact.";

/** A 403 from an admin-gated route: authenticated, but without the `role: "admin"` claim. */
export const NOT_ADMIN_MESSAGE =
  "This area is for Jones + Palmer administrators. If you should have access, ask an " +
  "existing admin to grant it, then sign out and back in.";

/**
 * Any 401. They all mean the same thing to a user — the token was missing, expired or revoked
 * — and the server keeps its own wording deliberately opaque, so echoing it would put protocol
 * vocabulary on screen instead of the one action that fixes it.
 */
export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please sign out and sign in again.";

/** `fetch` itself failed — offline, DNS, aborted connection. No response was received. */
export const NETWORK_FAILURE_MESSAGE = "Connection problem. Check your network and try again.";

/** Last resort for a 5xx, whose server-side message is deliberately opaque. */
export const GENERIC_FAILURE_MESSAGE = "Something went wrong. Please try again in a moment.";
